import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CircleUser,
  DownloadIcon,
  Edit,
  Eye,
  FileIcon,
  Star,
  Trash2,
  User,
} from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";
import CustomButton from "../customButton";
import CustomSwitch from "../switch";
import moment from "moment";

const tableStyle = {
  "&.MuiTableContainer-root": {
    backgroundColor: "#fff",
    borderRadius: "18px",
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
  },
  "&.MuiTableContainer-root .MuiTableHead-root": {
    background: "linear-gradient(90deg, #225B27, #053608)",
    fontWeight: "600",
    fontSize: "12px",
    borderRadius: "4px",
    color: "#ffff",
  },
  "&.MuiTableContainer-root .MuiTableHead-root .MuiTableRow-root .MuiTableCell-root":
    {
      // padding: "2px 12px",
      // color: "primary.text",
      fontSize: "12px",
      border: "1px solid #5c5b5b",
    },
  "& .MuiTableCell-root": {
    borderBottom: "1px solid #e0e0e0",
    padding: "6px",
    border: "1px solid rgb(206, 204, 204)",
    textAlign: "center",
  },
  "& .MuiTablePagination-toolbar": {
    background: "linear-gradient(90deg, #225B27, #053608)",
    color: "#ffff",
    minHeight: "40px",
  },
};

// Custom Status Chip Component
const CustomStatusChip = ({ status }) => {
  const getStatusConfig = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return {
          label: "Pending",
          color: "#FF9800", // Orange
          backgroundColor: "#FFF3E0",
          borderColor: "#FFB74D",
        };
      case "completed":
        return {
          label: "Completed",
          color: "#4CAF50", // Green
          backgroundColor: "#E8F5E8",
          borderColor: "#81C784",
        };
      case "onTime":
        return {
          label: "On Time",
          color: "#4CAF50", // Green
          backgroundColor: "#E8F5E8",
          borderColor: "#81C784",
        };
      case "late":
        return {
          label: "Late",
          color: "#B71C1C", // Green
          backgroundColor: "#F5E8E8",
          borderColor: "#81C784",
        };
      case "absent":
        return {
          label: "Absent",
          color: "#888", // Green
          backgroundColor: "red",
          borderColor: "red",
        };
      default:
        return {
          label: status,
          color: "#9E9E9E", // Gray
          backgroundColor: "#F5F5F5",
          borderColor: "#BDBDBD",
        };
    }
  };

  const config = getStatusConfig(status);

  return (
    <Chip
      label={config.label}
      size="small"
      sx={{
        backgroundColor: config.backgroundColor,
        color: config.color,
        border: `1px solid ${config.borderColor}`,
        fontWeight: 500,
        fontSize: "11px",
        height: "24px",
        "& .MuiChip-label": {
          px: 1.5,
        },
      }}
    />
  );
};

export default function PaginatedTable({
  tableWidth,
  tableHeader,
  tableData,
  displayRows,
  onEdit,
  onDelete,
  onViewClick,
  showPagination = true,
  rowsPerPage,
  currentPage,
  totalRecords,
  handleChangeRowsPerPage,
  handleChangePage,
  handleChangeStatus,
  handleDownload, 
  handleDownloadCompletedAttachments,
  handleViewWorkerDetail,
  handleViewAssigns,
  handleArchiveWorker,  
  handleViewSubmittedImages,
  handleViewSubtasks,
  handleRescheduleTask,
  handleViewSubtaskImages,
  handleViewSubtaskComments,
}) {
  const navigate = useNavigate();

  const renderCell = (row, val, index) => {
    switch (val) {
      case "checkbox":
        return (
          <TableCell padding="checkbox">
            <Checkbox size="small" />
          </TableCell>
        );

      case "id":
        return (
          <TableCell>
            <Stack direction={"row"} px={1} gap={2}>
              {row.id}
              <Star size={16} />
            </Stack>
          </TableCell>
        );

      case "submissionDate":
        return (
          <TableCell>
            {row.submittedAt
              ? moment(row.submittedAt).format("DD-MMM-YYYY")
              : row.createdAt
              ? moment(row.createdAt).format("DD-MMM-YYYY")
              : "-"}
          </TableCell>
        );

      case "title":
        return (
          <TableCell>
            <Typography fontSize={12} fontWeight={500}>
              {row.title || "-"}
            </Typography>
          </TableCell>
        );

      case "submittedBy":
        return (
          <TableCell>
            {row.submittedBy && row.submittedBy.length > 0 ? (
              <Stack direction="column" gap={0.5} alignItems="flex-start">
                {row.submittedBy.slice(0, 2).map((item, idx) => {
                  const worker = item.worker || item;
                  return (
                    <Stack
                      key={item._id || worker._id || idx}
                      direction="row"
                      gap={1}
                      alignItems="center"
                    >
                      <Avatar
                        sx={{
                          width: 20,
                          height: 20,
                          bgcolor: "primary.main",
                          fontSize: "10px",
                        }}
                      >
                        {worker.username?.[0]?.toUpperCase() || 
                         worker.firstName?.[0]?.toUpperCase() || 
                         "W"}
                      </Avatar>
                      <Typography fontSize={11} fontWeight={400}>
                        {worker.username || 
                         `${worker.firstName || ""} ${worker.lastName || ""}`.trim() || 
                         "N/A"}
                      </Typography>
                    </Stack>
                  );
                })}
                {row.submittedBy.length > 2 && (
                  <Typography fontSize={10} color="text.secondary">
                    +{row.submittedBy.length - 2} more
                  </Typography>
                )}
              </Stack>
            ) : (
              <Typography fontSize={12} color="text.secondary">
                No submissions
              </Typography>
            )}
          </TableCell>
        );

      case "employee":
        return (
          <TableCell>
            <Stack direction={"row"} px={1} gap={2} alignItems={"center"}>
              <Avatar
                src={row?.profileImage}
                alt={row?.worker?.firstName}
                sx={{ width: 24, height: 24, bgcolor: "#eee" }}
              />
              <Stack direction={"column"} gap={0.5} textAlign={"left"}>
                <Typography fontSize={12} fontWeight={400}>
                  {row?.workerId?.firstName} {row?.workerId?.lastName}
                </Typography>
                <Typography fontSize={12} fontWeight={400}>
                  {row?.workerId?.email}
                </Typography>
              </Stack>
            </Stack>
          </TableCell>
        );

      case "workerName":
        return (
          <TableCell>
            <Stack direction={"row"} px={1} gap={2} alignItems={"center"}>
              <Avatar
                src={row?.profileImage}
                alt={row?.firstName}
                sx={{ width: 24, height: 24, bgcolor: "#eee" }}
              />
              <Typography fontSize={12} fontWeight={400}>
                {row?.firstName} {row?.lastName}
              </Typography>
            </Stack>
          </TableCell>
        );

      case "viewAssigns":
        return (
          <TableCell>
            <CustomButton
              btnLabel="View Assigns"
              variant="outlinebutton"
              btnTextSize={10}
              handlePressBtn={() => handleViewAssigns(row)}
              btnTextColor="#225B27"
              startIcon={<User color="#000" strokeWidth={1.75} size={16} />}
            />
          </TableCell>
        );

      case "viewSubtasks":
        return (
          <TableCell>
            <CustomButton
              btnLabel="View Subtasks"
              variant="outlinebutton"
              btnTextSize={10}
              handlePressBtn={() => handleViewSubtasks(row)}
              btnTextColor="#225B27"
            />
          </TableCell>
        );

      case "download":
        return (
          <TableCell>
            <CustomButton
              btnLabel="Download"
              variant="webbutton"
              handlePressBtn={() => handleDownload(row)}
              startIcon={<DownloadIcon size={16} />}
            />
          </TableCell>
        );

      case "workerInfo":
        return (
          <TableCell>
            <Stack
              direction={"row"}
              px={1}
              gap={2}
              alignItems={"center"}
              width={"100%"}
              justifyContent={"center"}
            >
              <Avatar
                src={row?.worker?.profileImage}
                alt={row?.worker?.firstName}
                sx={{ width: 24, height: 24, bgcolor: "#eee" }}
              />
              <Typography fontSize={12} fontWeight={400}>
                {row?.worker?.firstName} {row?.worker?.lastName}
              </Typography>
            </Stack>
          </TableCell>
        );
      case "taskDueDate":
        return (
          <TableCell>{moment(row.taskDueDate).format("DD-MMM-YYYY")}</TableCell>
        );
      case "startDate":
        return (
          <TableCell>{moment(row.startDate).format("DD-MMM-YYYY")}</TableCell>
        );
      case "endDate":
        return (
          <TableCell>{moment(row.endDate).format("DD-MMM-YYYY")}</TableCell>
        );

      case "attechment_task":
        return (
          <TableCell>
            <Button
              sx={{
                backgroundColor: "#eee",
                borderRadius: "20px",
                padding: "5px 10px",
                fontSize: 10,
              }}
              startIcon={<CircleUser size={12} />}
            >
              {row.attecchment}
            </Button>
          </TableCell>
        );
      case "workerStatus":
        return (
          <TableCell>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              pl={2}
            >
              <CustomSwitch
                checked={row.status == "active" ? true : false}
                onChange={(e) => handleChangeStatus(e, row)}
              />
            </Box>
          </TableCell>
        );

      case "archived":
        return (
          <TableCell>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              pl={2}
            >
              <CustomSwitch
                checked={row.isArchived == true ? true : false}
                onChange={(e) => handleArchiveWorker(e, row)}
              />
            </Box>
          </TableCell>
        );


      case "ViewSubmittedImages":
        return (
          <TableCell>
            <CustomButton
              btnLabel="View Images"
              variant="outlinebutton"
              handlePressBtn={() => handleViewSubmittedImages(row)}
              btnTextColor="#225B27"
            />
          </TableCell>
        );

      case "breaksDisplay":
        return (
          <TableCell
            align="left"
            sx={{ verticalAlign: "top", py: 1, minWidth: 150 }}
          >
            {row.breaksDisplay}
          </TableCell>
        );

      case "commentsDisplay":
        return (
          <TableCell
            align="left"
            sx={{ verticalAlign: "top", py: 1, minWidth: 180 }}
          >
            {row.commentsDisplay}
          </TableCell>
        );

      case "status":
        return (
          <TableCell>
            <CustomStatusChip status={row.status} />
          </TableCell>
        );
      case "viewWorkerDetail":
        return (
          <TableCell>
            <CustomButton
              btnLabel="View Profile"
              variant="outlinebutton"
              btnTextSize={10}
              handlePressBtn={() => handleViewWorkerDetail(row)}
              btnTextColor="#225B27"
              startIcon={<User color="#000" strokeWidth={1.75} size={16} />}
            />
          </TableCell>
        );

      case "completedAttachments": {
        const isLandscape = row?.taskType === "landscape";
        const img = row?.image || {};
        const hasLandscapeData =
          (img.beforeWorkImages?.length || 0) > 0 ||
          (img.afterWorkImages?.length || 0) > 0 ||
          (img.additionalImages?.length || 0) > 0 ||
          (row?.beforeComment?.length || 0) > 0 ||
          (row?.afterComment?.length || 0) > 0;
        const hasSubtasks = Array.isArray(row?.subTask) && row.subTask.length > 0;
        const canDownload = isLandscape ? hasLandscapeData : hasSubtasks;

        return (
          <TableCell>
            {canDownload ? (
              <CustomButton
                btnLabel="Download  Attachments"
                variant="outlinebutton"
                btnTextSize={10}
                handlePressBtn={() => handleDownloadCompletedAttachments(row)}
                btnTextColor="#225B27"
                startIcon={<DownloadIcon size={16} />}
              />
            ) : (
              <Typography fontSize={12} color="#225B27" fontWeight={400}>
                No Completed Attachments
              </Typography>
            )}
          </TableCell>
        );
      }

      case "reschedule":
        return (
          <TableCell>
            <CustomButton
              btnLabel="Reschedule"
              variant="outlinebutton"
              btnTextSize={10}
              handlePressBtn={() => handleRescheduleTask?.(row)}
              btnTextColor="#225B27"
            />
          </TableCell>
        );

      case "taskType":
        return (
          <TableCell>
             <Typography fontSize={12} fontWeight={400}>  
              {row.taskType === "landscape" ? "Landscape" : row.taskType === "snowRemoval" ? "Snow Removal" : "-"}
             </Typography>
          </TableCell>
        );

      case "attachments":
        return (
          <TableCell>
            {row.attachment ? (
              <CustomButton
                btnLabel="Attachments"
                variant="webbutton"
                btnTextSize={10}
                // handlePressBtn={() => handleViewAttachments(row)}
                startIcon={<FileIcon size={16} />}
              />
            ) : (
              <Typography fontSize={12} color="#225B27" fontWeight={400}>
                No Attachments
              </Typography>
            )}
          </TableCell>
        );

      case "task_type":
        return (
          <TableCell>
            <Button
              sx={{
                backgroundColor: "#eee",
                borderRadius: "20px",
                padding: "2px 10px",
                fontSize: 10,
              }}
            >
              {row.taskType}
            </Button>
          </TableCell>
        );

      case "count":
        return <TableCell>{index + 1}</TableCell>;

      case "viewSubtaskImages":
        return (
          <TableCell>
            <CustomButton
              btnLabel="View Images"
              variant="outlinebutton"
              btnTextSize={10}
              handlePressBtn={() => handleViewSubtaskImages?.(row)}
              btnTextColor="#225B27"
            />
          </TableCell>
        );

      case "viewSubtaskComments":
        return (
          <TableCell>
            <CustomButton
              btnLabel="View Comments"
              variant="outlinebutton"
              btnTextSize={10}
              handlePressBtn={() => handleViewSubtaskComments?.(row)}
              btnTextColor="#225B27"
            />
          </TableCell>
        );

      case "subtaskWorker":
        return (
          <TableCell>
            <Stack
              direction="row"
              px={1}
              gap={1.5}
              alignItems="center"
              justifyContent="center"
            >

              <Stack direction="column" gap={0.25} textAlign="left">
                <Typography fontSize={12} fontWeight={500}>
                  {row?.worker
                    ? `${row.worker.firstName || ""} ${row.worker.lastName || ""}`.trim() || "--"
                    : "--"}
                </Typography>
                <Typography fontSize={11} color="text.secondary">
                  {row?.worker?.email || "-----"}
                </Typography>
              </Stack>
            </Stack>
          </TableCell>
        );

      case "workerDescription": {
        const description = row?.workerDescription?.trim() || "";
        const maxLength = 40;
        const truncated =
          description.length > maxLength
            ? `${description.slice(0, maxLength)}...`
            : description;

        return (
          <TableCell sx={{ maxWidth: 180 }}>
            {description ? (
              <Tooltip title={description} arrow placement="top">
                <Typography
                  fontSize={12}
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: 160,
                    mx: "auto",
                    cursor: "pointer",
                  }}
                >
                  {truncated}
                </Typography>
              </Tooltip>
            ) : (
              <Typography fontSize={12} color="text.secondary">
                —
              </Typography>
            )}
          </TableCell>
        );
      }

      case "actions":
        return (
          <TableCell align="center">
            <div
              style={{
                display: "flex",
                gap: 8,
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {onViewClick && (
                <IconButton
                  size="small"
                  onClick={() => onViewClick(row)}
                  aria-label="view"
                  sx={{
                    bgcolor: "#fff",
                    color: "primary.text",
                    border: "1px solid #eee",
                    borderRadius: "50%",
                    transition: "background-color 0.3s ease",
                    "&:hover": {
                      bgcolor: "primary.main",
                      color: "white",
                    },
                  }}
                >
                  <Eye size={14} />
                </IconButton>
              )}

              {onEdit && (
                <IconButton
                  size="small"
                  onClick={() => onEdit(row)}
                  aria-label="edit"
                  sx={{
                    bgcolor: "#fff",
                    color: "primary.text",
                    border: "1px solid #eee",
                    borderRadius: "50%",
                    transition: "background-color 0.3s ease",
                    "&:hover": {
                      bgcolor: "primary.main",
                      color: "white",
                    },
                  }}
                >
                  <Edit size={14} />
                </IconButton>
              )}

              {onDelete && (
                <IconButton
                  size="small"
                  onClick={() => onDelete(row)}
                  aria-label="delete"
                  sx={{
                    bgcolor: "#fff",
                    color: "primary.text",
                    border: "1px solid #eee",
                    borderRadius: "50%",
                    transition: "background-color 0.3s ease",
                    "&:hover": {
                      bgcolor: "error.main",
                      color: "white",
                    },
                  }}
                >
                  <Trash2 size={14} />
                </IconButton>
              )}
            </div>
          </TableCell>
        );

      default:
        return (
          <TableCell>
            <Typography
              fontSize={12}
              lineHeight="normal"
              style={{ whiteSpace: "pre-line" }}
            >
              {row[val]}
            </Typography>
          </TableCell>
        );
    }
  };

  return (
    <TableContainer sx={tableStyle}>
      <Table sx={{ width: tableWidth || "100%" }}>
        <TableHead>
          <TableRow>
            {tableHeader?.map((header) => (
              <TableCell
                key={header.id}
                align={header.align || "left"}
                padding={header.id === "checkbox" ? "checkbox" : "normal"}
              >
                <Typography
                  fontWeight={600}
                  fontSize={13}
                  py={1}
                  sx={{
                    textTransform: "uppercase",
                    color: "#ffff",
                    letterSpacing: "0.5px",
                  }}
                >
                  {header.title}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {tableData?.length > 0 ? (
            tableData?.map((row, index) => (
              <TableRow
                key={index}
                hover
                sx={{
                  backgroundColor: index % 2 === 0 ? "#F9FAFB" : "#FFFFFF", // Light Gray and White
                  transition: "background 0.3s ease",
                  // textTransform: "capitalize",
                  "&:hover": {
                    backgroundColor: "#E0F2FE", // Light Blue on hover
                  },
                }}
              >
                {displayRows.map((val) => renderCell(row, val, index))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={tableHeader?.length} align="center">
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  py={4}
                >
                  <Typography
                    fontSize={16}
                    fontWeight={400}
                    color="#225B27"
                    textAlign="center"
                  >
                    {" "}
                    No data found Please add some data
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {showPagination && (
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalRecords}
          rowsPerPage={rowsPerPage}
          page={currentPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            background: "#0000",
            borderTop: "1px solid #ccc",
            "& .MuiIconButton-root": {
              color: "white",
            },
            "& .MuiSvgIcon-root": {
              color: "white",
            },
          }}
        />
      )}
    </TableContainer>
  );
}
